// /opt/isp_erp/frontend/src/pages/admin/instalaciones/Installations.jsx
import React, { useState, useEffect } from "react";
import axios from "../../../api/axios";
import { mensajeError } from "../../../utils/mensajesError";
import { validarIdentificacion, validarEmail } from "../../../utils/validacionesEcuador";
import { useNavigate } from "react-router-dom";
import "./Installations.css";

// Componente de validación de identificación
const IdentificacionInput = ({ value = '', onChange, name = 'identificacion', tipo = 'CEDULA', required = true }) => {
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  // Función para validar según tipo
  const validateIdentificacion = (valor, tipoDoc) => {
    // Pasaporte: letras y números (misma regla que el servidor); cédula y RUC se validan más abajo
    if (tipoDoc === 'PASAPORTE') {
      const r = validarIdentificacion(valor, 'PASAPORTE');
      return r.ok ? { isValid: true, message: "" } : { isValid: false, message: valor ? r.mensaje : "Campo requerido" };
    }
    const cleanValue = valor.replace(/\D/g, '');

    if (!cleanValue) {
      return { isValid: false, message: "Campo requerido" };
    }

    if (tipoDoc === 'CEDULA') {
      if (cleanValue.length !== 10) {
        return { isValid: false, message: "La cédula debe tener 10 dígitos" };
      }

      const invalidSequences = [
        '0000000000', '1111111111', '2222222222', '3333333333',
        '4444444444', '5555555555', '6666666666', '7777777777',
        '8888888888', '9999999999'
      ];

      if (invalidSequences.includes(cleanValue)) {
        return { isValid: false, message: "Número de cédula inválido" };
      }

      const provincia = parseInt(cleanValue.substring(0, 2));
      if (provincia < 1 || provincia > 24) {
        return { isValid: false, message: "Provincia inválida" };
      }

      const tercerDigito = parseInt(cleanValue.substring(2, 3));
      if (tercerDigito > 5) {
        return { isValid: false, message: "Tercer dígito inválido" };
      }

      const coeficientes = [2, 1, 2, 1, 2, 1, 2, 1, 2];
      let suma = 0;

      for (let i = 0; i < 9; i++) {
        let valor = parseInt(cleanValue.charAt(i)) * coeficientes[i];
        if (valor >= 10) {
          valor = valor.toString().split('').reduce((a, b) => a + parseInt(b), 0);
        }
        suma += valor;
      }

      let digitoVerificador = 10 - (suma % 10);
      if (digitoVerificador === 10) digitoVerificador = 0;

      const digitoCalculado = parseInt(cleanValue.charAt(9));

      if (digitoVerificador !== digitoCalculado) {
        return { isValid: false, message: "Dígito verificador inválido" };
      }

    } else if (tipoDoc === 'RUC') {
      // ✅ VALIDACIÓN CORREGIDA: soporta persona natural, jurídica y entidad pública
      if (cleanValue.length !== 13) {
        return { isValid: false, message: "El RUC debe tener 13 dígitos" };
      }

      if (cleanValue.substring(10) !== '001') {
        return { isValid: false, message: "RUC debe terminar en 001" };
      }

      const provincia = parseInt(cleanValue.substring(0, 2));
      if (provincia < 1 || (provincia > 24 && provincia !== 30)) {
        return { isValid: false, message: "Provincia inválida" };
      }

      const tercerDigito = parseInt(cleanValue.substring(2, 3));

      // ✅ RUC de PERSONA NATURAL (tercer dígito 0-5): primeros 10 = cédula
      if (tercerDigito >= 0 && tercerDigito <= 5) {
        const cedulaPart = cleanValue.substring(0, 10);
        const cedulaValidation = validateIdentificacion(cedulaPart, 'CEDULA');
        if (!cedulaValidation.isValid) {
          return { isValid: false, message: "Los primeros 10 dígitos no forman una cédula válida" };
        }
        return { isValid: true, message: "" };
      }

      // ✅ RUC de PERSONA JURÍDICA (9) o ENTIDAD PÚBLICA (6): módulo 11
      if (tercerDigito === 6 || tercerDigito === 9) {
        const coeficientes = [4, 3, 2, 7, 6, 5, 4, 3, 2];
        let suma = 0;

        for (let i = 0; i < 9; i++) {
          suma += parseInt(cleanValue.charAt(i)) * coeficientes[i];
        }

        const residuo = suma % 11;
        let digitoVerificador = 11 - residuo;
        if (digitoVerificador === 11) digitoVerificador = 0;
        if (digitoVerificador === 10) digitoVerificador = 0;

        const digitoCalculado = parseInt(cleanValue.charAt(9));

        if (digitoVerificador !== digitoCalculado) {
          console.warn(`RUC ${cleanValue}: dígito verificador calculado ${digitoVerificador}, encontrado ${digitoCalculado}`);
        }

        return { isValid: true, message: "" };
      }

      return { isValid: false, message: "Tercer dígito del RUC inválido" };

    } else if (tipoDoc === 'PASAPORTE') {
      if (cleanValue.length < 6 || cleanValue.length > 9) {
        return { isValid: false, message: "El pasaporte debe tener entre 6 y 9 caracteres" };
      }
    }

    return { isValid: true, message: "" };
  };

  const handleChange = (e) => {
    const rawValue = e.target.value;
    let cleaned = rawValue;
    if (tipo !== 'PASAPORTE') {
      cleaned = rawValue.replace(/\D/g, '');
    } else {
      cleaned = rawValue.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    }

    onChange({
      target: {
        name: name,
        value: cleaned
      }
    });

    if (touched) {
      const validation = validateIdentificacion(cleaned, tipo);
      setError(validation.isValid ? '' : validation.message);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    const validation = validateIdentificacion(value, tipo);
    setError(validation.isValid ? '' : validation.message);
  };

  const getPlaceholder = () => {
    switch(tipo) {
      case 'CEDULA': return '0502168009';
      case 'RUC': return '0502168009001';
      case 'PASAPORTE': return 'A123456';
      default: return 'Ingrese número';
    }
  };

  const getMaxLength = () => {
    switch(tipo) {
      case 'CEDULA': return 10;
      case 'RUC': return 13;
      case 'PASAPORTE': return 20;
      default: return 20;
    }
  };

  const isValid = value && !error && touched;

  return (
    <div className="identificacion-input">
      <label className="input-label">
        {tipo} {required && <span className="required-star">*</span>}
      </label>
      <div className="input-wrapper">
        <input
          type="text"
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`input-field ${error && touched ? 'error' : isValid ? 'valid' : ''}`}
          placeholder={getPlaceholder()}
          maxLength={getMaxLength()}
        />
        {touched && (
          <div className="input-icon">
            {error ? (
              <span className="error-icon">❌</span>
            ) : isValid ? (
              <span className="valid-icon">✅</span>
            ) : null}
          </div>
        )}
      </div>
      {error && touched && (
        <p className="error-message">{error}</p>
      )}
    </div>
  );
};

export default function Installations() {
  const [installations, setInstallations] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [services, setServices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("PENDIENTE");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");

  // ✅ NUEVOS: filtros por fecha de instalación real
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const [form, setForm] = useState({
    tipo_identificacion: "CEDULA",
    identificacion: "",
    nombres: "",
    apellidos: "",
    telefono: "",
    email: "",
    direccion: "",
    ciudad: "",
    servicio: "",
    plan: "",
    tecnico_id: "",
    latitud: "",
    longitud: "",
    ficha: "",
    vineta: "",
    comisiones: ""
  });

  const navigate = useNavigate();

  // ✅ Función reutilizable para cargar instalaciones con filtros de fecha
  const fetchInstallations = async () => {
    try {
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;

      const res = await axios.get("/installations", { params });
      setInstallations(res.data);
    } catch (error) {
      console.error("Error cargando instalaciones:", error);
    }
  };

  // Cargar datos iniciales (técnicos, servicios) + primera carga de instalaciones
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [techRes, servicesRes] = await Promise.all([
          axios.get("/users/"),
          axios.get("/services?active_only=true")
        ]);

        const techs = techRes.data.filter(user =>
          user.roles?.some(role => role.name === "tecnico_campo")
        );
        setTechnicians(techs);
        setServices(servicesRes.data);

        // Cargar instalaciones sin filtros inicialmente
        const instRes = await axios.get("/installations");
        setInstallations(instRes.data);
      } catch (error) {
        console.error("Error cargando datos:", error);
        alert("Error al cargar datos");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // ✅ Refetch cuando cambian las fechas (con debounce)
  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => {
      fetchInstallations();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaDesde, fechaHasta]);

  // Cargar planes cuando se selecciona un servicio
  useEffect(() => {
    const fetchPlans = async () => {
      if (!selectedServiceId) {
        setPlans([]);
        return;
      }
      try {
        const res = await axios.get(`/services/plans?service_id=${selectedServiceId}&active_only=true`);
        setPlans(res.data);
      } catch (error) {
        console.error("Error cargando planes:", error);
      }
    };
    fetchPlans();
  }, [selectedServiceId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Estandarización a mayúsculas para nombres y apellidos
    if (name === 'nombres' || name === 'apellidos') {
      setForm({ ...form, [name]: value.toUpperCase() });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSave = async () => {
    if (!form.identificacion) {
      alert("El número de identificación es requerido");
      return;
    }

    // Solo se revisa lo que se escribe: al editar, un dato antiguo que no se tocó no bloquea
    const orig = form._orig;
    const cambioId = !orig || String(form.identificacion || "").trim() !== orig.identificacion || (form.tipo_identificacion || "") !== orig.tipo_identificacion;
    if (cambioId) {
      const idr = validarIdentificacion(form.identificacion, form.tipo_identificacion);
      if (!idr.ok) {
        alert(idr.mensaje);
        return;
      }
    }
    if (!orig || String(form.email || "").trim() !== orig.email) {
      const cr = validarEmail(form.email);
      if (!cr.ok) {
        alert(cr.mensaje);
        return;
      }
    }

    const { _orig, ...formLimpio } = form;
    const payload = {
      ...formLimpio,
      identificacion: form.identificacion,
      email: form.email ? String(form.email).trim() : null,
      tecnico_id: form.tecnico_id ? parseInt(form.tecnico_id) : null,
      servicio: selectedServiceId ? services.find(s => s.id === parseInt(selectedServiceId))?.service_type : form.servicio,
      plan: selectedPlanId ? plans.find(p => p.id === parseInt(selectedPlanId))?.name : form.plan,
      latitud: form.latitud ? parseFloat(form.latitud) : null,
      longitud: form.longitud ? parseFloat(form.longitud) : null
    };

    try {
      if (editId) {
        await axios.put(`/installations/${editId}`, payload);
      } else {
        await axios.post("/installations", payload);
      }

      await fetchInstallations();

      setShowForm(false);
      resetForm();

    } catch (err) {
      console.error(err.response?.data || err);
      alert(mensajeError(err, "Error guardando instalación"));
    }
  };

  const resetForm = () => {
    setForm({
      tipo_identificacion: "CEDULA",
      identificacion: "",
      nombres: "",
      apellidos: "",
      telefono: "",
      email: "",
      direccion: "",
      ciudad: "",
      servicio: "",
      plan: "",
      tecnico_id: "",
      latitud: "",
      longitud: "",
      ficha: "",
      vineta: "",
      comisiones: ""
    });
    setSelectedServiceId("");
    setSelectedPlanId("");
    setEditId(null);
  };

  const handleEdit = (inst) => {
    setForm({
      _orig: {
        tipo_identificacion: inst.tipo_identificacion || "CEDULA",
        identificacion: String(inst.identificacion || "").trim(),
        email: String(inst.email || "").trim(),
      },
      tipo_identificacion: inst.tipo_identificacion || "CEDULA",
      identificacion: inst.identificacion || "",
      nombres: inst.nombres || "",
      apellidos: inst.apellidos || "",
      telefono: inst.telefono || "",
      email: inst.email || "",
      direccion: inst.direccion || "",
      ciudad: inst.ciudad || "",
      servicio: inst.servicio || "",
      plan: inst.plan || "",
      tecnico_id: inst.tecnico_id || "",
      latitud: inst.latitud || "",
      longitud: inst.longitud || "",
      ficha: inst.ficha || "",
      vineta: inst.vineta || "",
      comisiones: inst.comisiones || ""
    });

    const service = services.find(s => s.service_type === inst.servicio);
    if (service) {
      setSelectedServiceId(service.id.toString());
      const plan = service.planes?.find(p => p.name === inst.plan);
      if (plan) {
        setSelectedPlanId(plan.id.toString());
      }
    }

    setEditId(inst.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar instalación?")) return;
    await axios.delete(`/installations/${id}`);
    await fetchInstallations();
  };

  const handleApprove = async (inst) => {
    if (!inst.tecnico_id) {
      alert("Debe asignar técnico antes de aprobar.");
      return;
    }

    await axios.put(`/installations/${inst.id}`, { estado: "APROBADA" });
    await fetchInstallations();
  };

  const handleInstall = (inst) => {
    navigate(`/clientes/instalaciones/dar-de-alta/${inst.id}`, {
      state: { installation: inst }
    });
  };

  // Filtrado por búsqueda de texto
  const filtered = installations.filter(i =>
    i.nombres?.toLowerCase().includes(search.toLowerCase()) ||
    i.apellidos?.toLowerCase().includes(search.toLowerCase()) ||
    i.identificacion?.includes(search)
  );

  const pendientes = filtered.filter(i => i.estado === "PENDIENTE");
  const aprobadas = filtered.filter(i => i.estado === "APROBADA");
  const instaladas = filtered.filter(i => i.estado === "INSTALADA");

  const currentData = activeTab === "PENDIENTE" ? pendientes : activeTab === "APROBADA" ? aprobadas : instaladas;

  const stats = {
    pendientes: pendientes.length,
    aprobadas: aprobadas.length,
    instaladas: instaladas.length,
    total: filtered.length
  };

  const getStatusClass = (estado) => {
    const classes = {
      PENDIENTE: 'estado-pendiente',
      APROBADA: 'estado-aprobada',
      INSTALADA: 'estado-instalada'
    };
    return classes[estado] || '';
  };

  // ✅ Helper para formatear la fecha de instalación
  const formatFechaInstalada = (fechaISO) => {
    if (!fechaISO) return null;
    try {
      const d = new Date(fechaISO);
      return {
        fecha: d.toLocaleDateString('es-EC', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }),
        hora: d.toLocaleTimeString('es-EC', {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="installations-loading-container">
        <div className="installations-loading-spinner"></div>
        <p>Cargando instalaciones...</p>
      </div>
    );
  }

  return (
    <div className="installations-container">
      <div className="installations-wrapper">
        {/* Header */}
        <div className="installations-header">
          <div className="header-left">
            <div className="header-icon">🔧</div>
            <div>
              <h1>Instalaciones</h1>
              <p>Gestione las instalaciones de servicios</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card total">
            <div className="stat-content">
              <div>
                <p className="stat-label">Total</p>
                <p className="stat-value">{stats.total}</p>
              </div>
              <div className="stat-icon total-icon">📦</div>
            </div>
          </div>

          <div className="stat-card pendiente">
            <div className="stat-content">
              <div>
                <p className="stat-label">Pendientes</p>
                <p className="stat-value">{stats.pendientes}</p>
              </div>
              <div className="stat-icon pendiente-icon">⏳</div>
            </div>
          </div>

          <div className="stat-card aprobada">
            <div className="stat-content">
              <div>
                <p className="stat-label">Aprobadas</p>
                <p className="stat-value">{stats.aprobadas}</p>
              </div>
              <div className="stat-icon aprobada-icon">✅</div>
            </div>
          </div>

          <div className="stat-card instalada">
            <div className="stat-content">
              <div>
                <p className="stat-label">Instaladas</p>
                <p className="stat-value">{stats.instaladas}</p>
              </div>
              <div className="stat-icon instalada-icon">🚀</div>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="main-card">
          {/* Tabs */}
          <div className="tabs-container">
            <button
              onClick={() => setActiveTab("PENDIENTE")}
              className={`tab-btn ${activeTab === "PENDIENTE" ? 'active' : ''}`}
            >
              <span className="tab-icon">⏳</span>
              Pendientes
              {stats.pendientes > 0 && (
                <span className="tab-count">{stats.pendientes}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("APROBADA")}
              className={`tab-btn ${activeTab === "APROBADA" ? 'active' : ''}`}
            >
              <span className="tab-icon">✅</span>
              Aprobadas
              {stats.aprobadas > 0 && (
                <span className="tab-count">{stats.aprobadas}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("INSTALADA")}
              className={`tab-btn ${activeTab === "INSTALADA" ? 'active' : ''}`}
            >
              <span className="tab-icon">🚀</span>
              Instaladas
              {stats.instaladas > 0 && (
                <span className="tab-count">{stats.instaladas}</span>
              )}
            </button>
          </div>

          {/* Toolbar */}
          <div className="toolbar">
            <div className="toolbar-row toolbar-row-top">
              <div className="toolbar-left">
                <button
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                  }}
                  className="btn-primary"
                >
                  ➕ Nueva Instalación
                </button>

                <button
                  onClick={() => navigate("/instalaciones/carga-masiva")}
                  className="btn-success"
                >
                  📤 Carga Masiva
                </button>
              </div>

              <div className="toolbar-right">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Buscar cliente..."
                    className="search-input"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      className="search-clear"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="toolbar-row toolbar-row-filters">
              <div className="filter-group">
                <span className="filter-group-label">📅 Filtrar por fecha de instalación:</span>

                <div className="date-filter-inline">
                  <label>Desde</label>
                  <input
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    className="date-input"
                  />
                </div>

                <div className="date-filter-inline">
                  <label>Hasta</label>
                  <input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    className="date-input"
                  />
                </div>

                {(fechaDesde || fechaHasta) && (
                  <button
                    className="btn-clear-date"
                    onClick={() => { setFechaDesde(""); setFechaHasta(""); }}
                  >
                    ✕ Limpiar
                  </button>
                )}
              </div>

              <div className="results-count">
                <span className="filter-icon">🔍</span>
                <span>{currentData.length} registros</span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-container">
            <table className="installations-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Comisiones</th>
                  <th>Contacto</th>
                  <th>Servicio/Plan</th>
                  <th>Técnico</th>
                  <th>Fecha Instalación</th>
                  <th>Estado</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="empty-state">
                      <div className="empty-icon">📦</div>
                      <p className="empty-title">No hay instalaciones</p>
                      <p className="empty-subtitle">Crea una nueva instalación para comenzar</p>
                    </td>
                  </tr>
                ) : (
                  currentData.map(inst => {
                    const fechaInfo = formatFechaInstalada(inst.fecha_instalada);
                    return (
                      <tr key={inst.id} className="installation-row">
                        <td>
                          <div className="cliente-cell">
                            <div className="cliente-avatar">
                              {inst.nombres?.charAt(0) || '👤'}
                            </div>
                            <div>
                              <p className="cliente-nombre">
                                {inst.nombres} {inst.apellidos}
                              </p>
                              <p className="cliente-id">
                                {inst.tipo_identificacion}: {inst.identificacion}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td>
                          {inst.comisiones ? (
                            <div className="comisiones-cell">
                              <span className="comisiones-icon">💰</span>
                              <span className="comisiones-text" title={inst.comisiones}>
                                {inst.comisiones.length > 30
                                  ? inst.comisiones.substring(0, 30) + '...'
                                  : inst.comisiones}
                              </span>
                            </div>
                          ) : (
                            <span className="comisiones-empty">-</span>
                          )}
                        </td>

                        <td>
                          <div className="contacto-cell">
                            <div className="contacto-item">
                              <span className="contacto-icon">📞</span>
                              {inst.telefono || 'N/A'}
                            </div>
                            <div className="contacto-item">
                              <span className="contacto-icon">✉️</span>
                              {inst.email || 'N/A'}
                            </div>
                            <div className="contacto-item">
                              <span className="contacto-icon">📍</span>
                              {inst.ciudad || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="servicio-cell">
                            <div className="servicio-tipo">
                              {inst.servicio === 'INTERNET' && <span className="servicio-icon internet">🌐</span>}
                              {inst.servicio === 'TV' && <span className="servicio-icon tv">📺</span>}
                              {inst.servicio === 'DUOPACK' && <span className="servicio-icon duopack">📦</span>}
                              <span className="servicio-nombre">{inst.servicio || 'N/A'}</span>
                            </div>
                            <p className="servicio-plan">{inst.plan || 'Sin plan'}</p>
                          </div>
                        </td>
                        <td>
                          {inst.tecnico_id ? (
                            <span className="tecnico-nombre">
                              {technicians.find(t => t.id === inst.tecnico_id)?.username || 'Asignado'}
                            </span>
                          ) : (
                            <span className="tecnico-no">No asignado</span>
                          )}
                        </td>

                        {/* ✅ NUEVA COLUMNA: Fecha de instalación real */}
                        <td>
                          {fechaInfo ? (
                            <div className="fecha-instalada">
                              <div className="fecha-dia">{fechaInfo.fecha}</div>
                              <div className="fecha-hora">{fechaInfo.hora}</div>
                            </div>
                          ) : (
                            <span className="fecha-empty">—</span>
                          )}
                        </td>

                        <td>
                          <span className={`estado-badge ${getStatusClass(inst.estado)}`}>
                            {inst.estado}
                          </span>
                        </td>
                        <td className="text-right">
                          <div className="acciones-cell">
                            {activeTab === "PENDIENTE" && (
                              <>
                                <button
                                  onClick={() => handleEdit(inst)}
                                  className="action-btn edit"
                                  title="Editar"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => handleApprove(inst)}
                                  className={`action-btn approve ${!inst.tecnico_id ? 'disabled' : ''}`}
                                  title="Aprobar"
                                  disabled={!inst.tecnico_id}
                                >
                                  ✅
                                </button>
                                <button
                                  onClick={() => handleDelete(inst.id)}
                                  className="action-btn delete"
                                  title="Eliminar"
                                >
                                  🗑️
                                </button>
                              </>
                            )}

                            {activeTab === "APROBADA" && (
                              <>
                                <button
                                  onClick={() => handleInstall(inst)}
                                  className="action-btn install"
                                  title="Instalar"
                                >
                                  🚀
                                </button>
                                <button
                                  onClick={() => handleDelete(inst.id)}
                                  className="action-btn delete"
                                  title="Eliminar"
                                >
                                  🗑️
                                </button>
                              </>
                            )}

                            {activeTab === "INSTALADA" && (
                              <span className="completed-badge">Completada</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <p className="pagination-info">
              Mostrando {currentData.length} de {filtered.length} registros
            </p>
            <div className="pagination-controls">
              <button className="pagination-btn" disabled>◀</button>
              <span className="pagination-current">1</span>
              <button className="pagination-btn" disabled>▶</button>
            </div>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="modal-overlay">
            <div className="modal-content modal-large">
              <div className="modal-header">
                <div className="modal-header-left">
                  <div className="modal-header-icon">🔧</div>
                  <div>
                    <h3>{editId ? 'Editar Instalación' : 'Nueva Instalación'}</h3>
                    <p>Complete los datos de la instalación</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="modal-close"
                >
                  ✕
                </button>
              </div>

              <div className="modal-body">
                <div className="form-grid">
                  {/* Tipo Identificación */}
                  <div className="form-group">
                    <label className="form-label">Tipo</label>
                    <select
                      name="tipo_identificacion"
                      value={form.tipo_identificacion}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="CEDULA">Cédula</option>
                      <option value="RUC">RUC</option>
                      <option value="PASAPORTE">Pasaporte</option>
                    </select>
                  </div>

                  {/* Identificación con validación */}
                  <div className="form-group">
                    <IdentificacionInput
                      value={form.identificacion}
                      onChange={handleChange}
                      name="identificacion"
                      tipo={form.tipo_identificacion}
                    />
                  </div>

                  {/* Nombres y Apellidos */}
                  <div className="form-group">
                    <label className="form-label">Nombres</label>
                    <input
                      name="nombres"
                      placeholder="Nombres"
                      value={form.nombres}
                      onChange={handleChange}
                      className="form-input uppercase"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Apellidos</label>
                    <input
                      name="apellidos"
                      placeholder="Apellidos"
                      value={form.apellidos}
                      onChange={handleChange}
                      className="form-input uppercase"
                    />
                  </div>

                  {/* Teléfono y Email */}
                  <div className="form-group">
                    <label className="form-label">Teléfono</label>
                    <input
                      name="telefono"
                      placeholder="Teléfono"
                      value={form.telefono}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={form.email}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  {/* ✅ Dirección - AHORA SOLO MANUAL, SIN GOOGLE MAPS */}
                  <div className="form-group full-width">
                    <label className="form-label">Dirección</label>
                    <input
                      name="direccion"
                      placeholder="Escriba la dirección manualmente"
                      value={form.direccion}
                      onChange={handleChange}
                      className="form-input"
                    />
                    <p className="field-hint">
                      📍 Las coordenadas GPS se tomarán automáticamente cuando el técnico cierre el ticket de instalación.
                    </p>
                  </div>

                  {/* Ciudad */}
                  <div className="form-group">
                    <label className="form-label">Ciudad</label>
                    <input
                      name="ciudad"
                      placeholder="Ciudad"
                      value={form.ciudad}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  {/* Servicio */}
                  <div className="form-group">
                    <label className="form-label">Servicio</label>
                    <select
                      value={selectedServiceId}
                      onChange={(e) => setSelectedServiceId(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Seleccione</option>
                      {services.map(service => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Plan */}
                  <div className="form-group">
                    <label className="form-label">Plan</label>
                    <select
                      value={selectedPlanId}
                      onChange={(e) => setSelectedPlanId(e.target.value)}
                      className="form-select"
                      disabled={!selectedServiceId}
                    >
                      <option value="">Seleccione</option>
                      {plans.map(plan => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - ${plan.price}
                          {plan.speed_mbps && ` (${plan.speed_mbps}Mbps)`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Técnico */}
                  <div className="form-group">
                    <label className="form-label">Técnico</label>
                    <select
                      name="tecnico_id"
                      value={form.tecnico_id}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">Seleccione</option>
                      {technicians.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.username}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Comisiones */}
                  <div className="form-group full-width">
                    <label className="form-label">
                      Comisiones / Observaciones
                      <span className="optional-badge">(Opcional)</span>
                    </label>
                    <textarea
                      name="comisiones"
                      placeholder="Ej: Observaciones, comisión por referido..."
                      value={form.comisiones}
                      onChange={handleChange}
                      rows="3"
                      className="form-textarea"
                    />
                    <p className="field-hint">
                      Registre información sobre comisiones, observaciones para el técnico/vendedor
                    </p>
                  </div>

                  {/* Ficha */}
                  <div className="form-group full-width">
                    <label className="form-label">Ficha</label>
                    <textarea
                      name="ficha"
                      placeholder="Información de Contrato..."
                      value={form.ficha}
                      onChange={handleChange}
                      rows="3"
                      className="form-textarea"
                    />
                    <p className="field-hint">Detalles de Contrato.</p>
                  </div>

                  {/* Viñeta */}
                  <div className="form-group full-width">
                    <label className="form-label">Viñeta / Nota Rápida</label>
                    <input
                      name="vineta"
                      placeholder="Nota corta visible para el cliente..."
                      value={form.vineta}
                      onChange={handleChange}
                      className="form-input"
                    />
                    <p className="field-hint">Información breve que se mostrará al cliente</p>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="btn-primary"
                >
                  {editId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
